import { ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';

import List from '@pelckmans/elementis/components/list';

import { NodeWithoutDescription } from '../../../../../../../api/table-of-content';
import { UserMaterial } from '../../../../../../../api/user-material';
import { MaterialIcon } from '../../../../../components/MaterialIcon';

export type NodeWithMaterial = NodeWithoutDescription & {
  material?: UserMaterial[];
  path?: string[];
};

type Level = NonNullable<ComponentProps<typeof List.Item>['level']>;

export type SelectionState = 'checked' | 'indeterminate';

type Props = {
  node: NodeWithMaterial;
  level: number;
  renderBranches: (nodes: NodeWithMaterial[], level?: number) => JSX.Element[];
  onSelectionChange: (nodeId: string, rowId: string) => void;
  selection: Record<string, SelectionState>;
};

const BASE = 'PAGES.TOC_LEVEL.MODALS.COPY_MATERIAL.NODE_ITEMS';

export default function NodeBranch({ node, level, renderBranches, onSelectionChange, selection }: Props) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<boolean>(() => level === 0);

  function onCollapse() {
    setCollapsed(prev => !prev);
  }

  const avatar = node.image
    ? { src: node.image, name: t(`${BASE}.AVATAR_A11Y_LABEL`, { nodeTitle: node.title }), title: node.title }
    : undefined;

  return (
    <>
      <List.Item
        as="checkbox"
        key={node.id}
        title={node.title}
        subText={node.prefix}
        subTextPlacement="top"
        level={level as Level}
        onToggleCollapse={onCollapse}
        collapsed={collapsed}
        collapseButtonLabelText={t(`${BASE}.COLLAPSE_BUTTON.A11Y_LABEL`)}
        indeterminate={selection[node.id] === 'indeterminate'}
        checked={selection[node.id] === 'checked'}
        avatar={avatar}
        onClick={() => {
          onSelectionChange(node.id, node.id);
          setCollapsed(false);
        }}
        data-testid={`node-${node.id}`}
      />
      {!collapsed &&
        node.material?.map(item => (
          <List.Item
            as="checkbox"
            key={item.id}
            title={item.name}
            indeterminate={selection[item.id] === 'indeterminate'}
            checked={selection[item.id] === 'checked'}
            level={(level + 1) as Level}
            icon={<MaterialIcon materialType="user-material" />}
            onClick={() => {
              onSelectionChange(node.id, item.id);
            }}
            data-testid={`material-${item.id}`}
          />
        ))}
      {!collapsed && node.nodes?.length ? renderBranches(node.nodes, level + 1) : undefined}
    </>
  );
}
